import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import Loading from "../../../../components/shared/layout/Loading";
import { LoadingButton } from "../../../../components/shared/LoadingButton";
import {
  useChangeOsadReasonCode,
  useGetOsadReasonCodes,
} from "../../../../hooks/react-query/outbound/freightDetails/useGetOsadDetails";
import { useSelectedServiceCenter } from "../../../../hooks/useSelectedServiceCenter";
import { toast } from "../../../../utils/snackbarHelper";
import { toastMessage } from "../../../../constants/strings";

interface OsadShipmentItem {
  id: number;
  proNumber: string;
  reasonId: number | null;
}

interface EditReasonOsadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  shipments: OsadShipmentItem[];
}

export const EditReasonOsadDialog = ({
  isOpen,
  onClose,
  shipments,
}: EditReasonOsadDialogProps) => {
  const initialReasonId = shipments[0]?.reasonId ?? null;
  const [reasonId, setReasonId] = useState<number | null>(initialReasonId);
  const [serviceCenter] = useSelectedServiceCenter();

  const { data: reasons, isLoading: isLoadingReasons } =
    useGetOsadReasonCodes(isOpen);

  const handleClose = () => {
    setReasonId(null);
    onClose();
  };

  const { mutate: changeReasonCode, isPending } = useChangeOsadReasonCode({
    sicId: serviceCenter.id,
    onSuccess: () => {
      toast("Reason code updated successfully.", { variant: "success" });
      handleClose();
    },
    onError: () => {
      toast(toastMessage.generics.error, { variant: "error" });
    },
    method: "put",
  });

  const handleConfirm = () => {
    if (reasonId === null) return;
    changeReasonCode({
      reasonId,
      shipmentIds: shipments.map((s) => s.id),
    });
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        component={Stack}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        Edit Reason for Pro # {shipments.map((s) => s.proNumber).join(", ")}
        <IconButton
          aria-label="Close dialog"
          onClick={handleClose}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <FormControl sx={{ pt: 1 }}>
          <FormLabel id="osad-option-label">Select an option</FormLabel>
          <RadioGroup
            aria-labelledby="osad-option-label"
            value={reasonId ?? ""}
            onChange={(event) => {
              const selectedReasonId = Number(event.target.value);
              setReasonId(selectedReasonId);
            }}
            sx={{
              mt: 3,
              gap: 3,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {!isLoadingReasons ? (
              (reasons ?? []).map((reason) => (
                <FormControlLabel
                  key={reason.id}
                  value={reason.id}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography fontSize={14} fontWeight={600}>
                        {reason.name}
                      </Typography>
                      {reason.description && (
                        <Typography fontSize={14} color="text.secondary">
                          {reason.description}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              ))
            ) : (
              <Loading />
            )}
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          isDisabled={reasonId === null || reasonId === initialReasonId}
          isLoading={isPending}
          loadingLabel="Confirming..."
          onClick={handleConfirm}
        >
          Confirm
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
